import type { TAnnotationData } from "types/aiAgent.type"
import type { PDFInstance, NavigationResult } from "types/viewer.type"
import type { Core } from "@pdftron/webviewer"

/**
 * ViewerNavigationService
 *
 * Enhanced service for PDF navigation and text highlighting using Apryse WebViewer.
 *
 * Key features for text highlighting:
 * 1. Uses TextHighlightAnnotation exclusively for proper text highlighting
 * 2. Supports multiple coordinate formats:
 *    - Quads: Array of 4-point polygons for precise character-level highlighting
 *    - Bounding boxes: Traditional x,y,width,height coordinates
 * 3. Automatically creates optimized quads for multi-line text
 * 4. Validates coordinates to ensure they're within page bounds
 * 5. Persistent highlighting that remains until a new paragraph reference is clicked
 * 6. Transparent background with blue border (#0047FF) for visual clarity
 *
 * For optimal text highlighting, the API should provide:
 * - quads: Array of precise character/word quadrilaterals
 * - char_boxes: Character-level coordinates for fine-grained control
 *
 * Current implementation gracefully handles legacy bounding box data
 * while supporting enhanced coordinate data when available.
 * Highlights persist until a new paragraph reference is selected.
 */

export class ViewerNavigationService {
	private instance: PDFInstance | null = null
	private activeHighlights: Set<Core.Annotations.Annotation> = new Set()
	private highlightTimer: ReturnType<typeof setTimeout> | null = null

	setInstance(instance: PDFInstance): void {
		this.instance = instance
		this.setupClickListener()
	}

	navigateToPage(pageNumber: string, quotation?: string): NavigationResult {
		if (!this.instance) {
			return { success: false, error: "PDF viewer not initialized" }
		}

		try {
			const docViewer = this.instance.Core.documentViewer
			const pageNum = parseInt(pageNumber, 10)

			// Validate page number
			if (isNaN(pageNum)) {
				return { success: false, error: `Invalid page number: ${pageNumber}` }
			}

			// Get total pages and validate
			const totalPages = docViewer.getPageCount()
			if (pageNum < 1 || pageNum > totalPages) {
				return {
					success: false,
					error: `Reference to page ${pageNum} found, but document only has ${totalPages} pages`,
				}
			}

			// docViewer.setCurrentPage(pageNum, true)

			// If quotation is provided, search for it and highlight
			if (quotation) {
				this.searchAndHighlightQuotation(quotation, pageNum)
			}

			return { success: true }
		} catch (error) {
			return { success: false, error: `Navigation error: ${error}` }
		}
	}

	/**
	 * Search for a quotation on a specific page and highlight it using text extraction and positioning
	 */
	private async searchAndHighlightQuotation(quotation: string, pageNumber: number): Promise<void> {
		if (!this.instance) {
			console.warn("PDF viewer not initialized for quotation search")
			return
		}

		// Clear any existing highlights first
		this.clearExistingHighlights()

		const { documentViewer } = this.instance.Core

		try {
			const document = documentViewer.getDocument()
			const pageText = await document.loadPageText(pageNumber)

			// Normalize text for better matching
			const normalizeText = (text: string) =>
				text
					.replace(/\.{3,}/g, "") // Remove ellipses
					.replace(/…/g, "") // Remove unicode ellipsis
					.replace(/\s+/g, " ") // Multiple spaces to single
					.replace(/[\r\n]+/g, " ") // Line breaks to spaces
					.trim()
					.toLowerCase()

			const normalizedQuotation = normalizeText(quotation)
			const normalizedPageText = normalizeText(pageText)

			let quotationIndex = normalizedPageText.indexOf(normalizedQuotation)

			// If exact match fails, try first few words
			if (quotationIndex === -1) {
				const words = normalizedQuotation.split(" ").filter((w) => w.length > 2)
				if (words.length >= 3) {
					const firstWords = words.slice(0, 3).join(" ")
					quotationIndex = normalizedPageText.indexOf(firstWords)
					console.log("Trying first 3 words:", firstWords, "Found at:", quotationIndex)
				}
			}

			if (quotationIndex === -1) {
				console.warn(`Quotation not found on page ${pageNumber}`)
				this.tryPartialMatch(quotation, pageText, pageNumber)
				return
			}

			// Map back to original text index (simplified)
			const originalIndex = Math.max(0, Math.floor((quotationIndex / normalizedPageText.length) * pageText.length))
			const endIndex = originalIndex + Math.min(quotation.length, normalizedQuotation.length)

			const quads = await document.getTextPosition(pageNumber, originalIndex, endIndex)

			if (quads && quads.length > 0) {
				this.createHighlightFromQuads(quads, quotation, pageNumber)
				// Scroll to the highlighted quotation
				this.scrollToQuads(quads, pageNumber)
			} else {
				console.warn("Could not get text position, trying partial match")
				this.tryPartialMatch(quotation, pageText, pageNumber)
			}
		} catch (error) {
			console.error("Error in search and highlight:", error)
			this.tryPartialMatch(quotation, "", pageNumber)
		}
	}

	/**
	 * Try partial matching for better search results
	 */
	private async tryPartialMatch(quotation: string, pageText: string, pageNumber: number): Promise<void> {
		const cleanedQuotation = quotation
			.replace(/\.{3,}/g, "")
			.replace(/…/g, "")
			.trim()
		const words = cleanedQuotation.split(/\s+/).filter((w) => w.length > 2)

		if (words.length < 2) {
			console.warn(`Cannot find quotation: "${cleanedQuotation}"`)
			return
		}

		// Try first and last words
		const firstWord = words[0].toLowerCase()
		const lastWord = words[words.length - 1].toLowerCase()
		const pageTextLower = pageText.toLowerCase()

		const firstIndex = pageTextLower.indexOf(firstWord)
		const lastIndex = pageTextLower.indexOf(lastWord)

		if (firstIndex !== -1 && lastIndex !== -1 && lastIndex > firstIndex) {
			console.log("Found partial match using first/last words")
			const startIndex = firstIndex
			const endIndex = lastIndex + lastWord.length

			try {
				const document = this.instance!.Core.documentViewer.getDocument()
				const quads = await document.getTextPosition(pageNumber, startIndex, endIndex)

				if (quads && quads.length > 0) {
					this.createHighlightFromQuads(quads, quotation, pageNumber)
					// Scroll to the highlighted quotation
					this.scrollToQuads(quads, pageNumber)
					return
				}
			} catch (error) {
				console.warn("Partial match positioning failed:", error)
			}
		}

		// If we couldn't find or highlight the text, at least scroll to the page
		if (this.instance) {
			console.warn(`Could not find or highlight quotation: "${cleanedQuotation}", scrolling to page instead`)
			this.instance.Core.documentViewer.setCurrentPage(pageNumber, true)
		}
	}

	/**
	 * Create highlight from quads data
	 */
	private createHighlightFromQuads(quads: unknown[], quotation: string, pageNumber: number): void {
		if (!this.instance) return

		try {
			const { annotationManager, Annotations, documentViewer } = this.instance.Core

			// Get page information for bounds checking
			const doc = documentViewer.getDocument()
			const pageInfo = doc.getPageInfo(pageNumber)

			const validQuads = quads.filter((quad) => {
				const quadData = quad as {
					x1?: number
					y1?: number
					x2?: number
					y2?: number
					x3?: number
					y3?: number
					x4?: number
					y4?: number
				}
				return (
					quadData &&
					typeof quadData.x1 === "number" &&
					typeof quadData.y1 === "number" &&
					typeof quadData.x2 === "number" &&
					typeof quadData.y2 === "number" &&
					typeof quadData.x3 === "number" &&
					typeof quadData.y3 === "number" &&
					typeof quadData.x4 === "number" &&
					typeof quadData.y4 === "number"
				)
			})

			if (validQuads.length === 0) return

			// Find the overall bounding box of all quads to create one continuous highlight
			const allX = validQuads.flatMap((quad) => {
				const q = quad as {
					x1: number
					y1: number
					x2: number
					y2: number
					x3: number
					y3: number
					x4: number
					y4: number
				}
				return [q.x1, q.x2, q.x3, q.x4]
			})
			const allY = validQuads.flatMap((quad) => {
				const q = quad as {
					x1: number
					y1: number
					x2: number
					y2: number
					x3: number
					y3: number
					x4: number
					y4: number
				}
				return [q.y1, q.y2, q.y3, q.y4]
			})

			const minX = Math.min(...allX)
			const maxX = Math.max(...allX)
			const minY = Math.min(...allY)
			const maxY = Math.max(...allY)

			// Add 4px padding around the text for better visual appearance, but ensure it stays within page bounds
			const padding = 4
			const finalX = Math.max(0, minX - padding)
			const finalY = Math.max(0, minY - padding)
			const finalWidth = Math.min(maxX - minX + padding * 2, pageInfo.width - finalX)
			const finalHeight = Math.min(maxY - minY + padding * 2, pageInfo.height - finalY)

			// Create a single rectangle annotation that covers the entire text area with padding
			const highlight = new Annotations.RectangleAnnotation({
				PageNumber: pageNumber,
				X: finalX,
				Y: finalY,
				Width: finalWidth,
				Height: finalHeight,
			})

			// Style with semi-transparent fill and blue border
			highlight.FillColor = new Annotations.Color(0, 0, 0, 0) // Transparent fill
			highlight.StrokeColor = new Annotations.Color(0, 71, 255, 1) // Blue border #0047FF
			highlight.StrokeThickness = 2
			highlight.setCustomData("selectable", "false")
			highlight.Listable = false
			highlight.setContents(quotation)

			annotationManager.addAnnotation(highlight)
			annotationManager.redrawAnnotation(highlight)
			this.activeHighlights.add(highlight)

			this.startHighlightTimer()
		} catch (error) {
			console.error("Error creating highlight from quads:", error)
		}
	}

	navigateToParagraph(annotationData: TAnnotationData): NavigationResult {
		if (!this.instance) {
			return { success: false, error: "PDF viewer not initialized" }
		}

		try {
			// Clear any existing highlights and timer only when navigating to a different paragraph
			this.clearExistingHighlights()

			const { documentViewer, annotationManager, Annotations } = this.instance.Core

			// Get page info for bounds checking
			const doc = documentViewer.getDocument()
			const pageInfo = doc.getPageInfo(annotationData.pageNumber)

			// Always use TextHighlightAnnotation for proper text highlighting
			const annot = new Annotations.TextHighlightAnnotation({
				PageNumber: annotationData.pageNumber,
			})

			// Determine quads to use for highlighting
			let quadsToUse: Core.Math.Quad[] = []

			// If no valid quads, try to create optimized quads from bounding box
			if (quadsToUse.length === 0) {
				quadsToUse = this.createOptimizedQuadsFromBounds(annotationData, pageInfo)
			}

			// Set the quads for text highlighting
			if (quadsToUse.length > 0) {
				annot.Quads = quadsToUse
			} else {
				// Fallback: create a simple highlight using basic rectangle if coordinates are valid
				const { x, y, width, height } = annotationData
				if (x >= 0 && y >= 0 && width > 0 && height > 0) {
					console.warn("Using fallback rectangle annotation instead of quads")

					// Create a simple rect annotation instead
					const rectAnnot = new Annotations.RectangleAnnotation({
						PageNumber: annotationData.pageNumber,
						X: x,
						Y: y,
						Width: width,
						Height: height,
					})
					// Style the rectangle annotation - transparent background with blue border
					rectAnnot.FillColor = new Annotations.Color(0, 0, 0, 0) // Transparent background
					rectAnnot.StrokeColor = new Annotations.Color(0, 71, 255, 1) // Blue border #0047FF
					rectAnnot.StrokeThickness = 2 // Border thickness

					// Disable interaction with the annotation
					rectAnnot.setCustomData("selectable", "false")
					rectAnnot.Listable = false // Hide from annotation list

					// Add content if provided
					if (annotationData.content) {
						rectAnnot.setContents(annotationData.content)
					}

					// Add annotation ID for future reference
					if (annotationData.id) {
						rectAnnot.Id = annotationData.id
					}

					// Add the rectangle annotation
					annotationManager.addAnnotation(rectAnnot)
					annotationManager.redrawAnnotation(rectAnnot)

					// Track this annotation for cleanup
					this.activeHighlights.add(rectAnnot)

					// Scroll to the specific coordinates using WebViewer's built-in method
					this.scrollToCoordinates(annotationData)

					// Start timer to clear highlights after 10 seconds
					this.startHighlightTimer()

					return { success: true }
				} else {
					return {
						success: false,
						error: `Could not create valid quads for text highlighting. Invalid coordinates: x=${x}, y=${y}, width=${width}, height=${height}`,
					}
				}
			}
			// Style the text highlight annotation - transparent background with blue border
			annot.Color = new Annotations.Color(0, 0, 0, 0) // Transparent background
			annot.StrokeColor = new Annotations.Color(0, 71, 255, 1) // Blue border #0047FF
			annot.StrokeThickness = 2 // Border thickness

			// Disable interaction with the annotation
			annot.setCustomData("selectable", "false")
			annot.Listable = false // Hide from annotation list

			// Add content if provided
			if (annotationData.content) {
				annot.setContents(annotationData.content)
			}

			// Add annotation ID for future reference
			if (annotationData.id) {
				annot.Id = annotationData.id
			}

			// Add the annotation
			annotationManager.addAnnotation(annot)
			annotationManager.redrawAnnotation(annot)

			// Track this annotation for cleanup
			this.activeHighlights.add(annot)

			// Scroll to the specific coordinates using WebViewer's built-in method
			this.scrollToCoordinates(annotationData)

			// Start timer to clear highlights after 10 seconds
			this.startHighlightTimer()

			return { success: true }
		} catch (error) {
			return { success: false, error: `Text highlighting error: ${error}` }
		}
	}

	/**
	 * Creates optimized quads from bounding box coordinates
	 * This method attempts to create reasonable text highlighting quads even from simple bounds
	 */
	private createOptimizedQuadsFromBounds(
		annotationData: TAnnotationData,
		pageInfo: { width: number; height: number },
	): Core.Math.Quad[] {
		if (!this.instance) {
			return []
		}

		try {
			// Ensure coordinates are within bounds
			const x = Math.max(0, Math.min(annotationData.x, pageInfo.width))
			const y = Math.max(0, Math.min(annotationData.y, pageInfo.height))
			const width = Math.min(annotationData.width, pageInfo.width - x)
			const height = Math.min(annotationData.height, pageInfo.height - y)

			// Validate that we have positive dimensions
			if (width <= 0 || height <= 0) {
				console.warn("Invalid dimensions for quad creation:", { width, height, x, y })
				return []
			}

			// For better text highlighting, we can create multiple quads if the text spans multiple lines
			const quads: Core.Math.Quad[] = []

			// Estimate line height (this is a heuristic, real implementation might need font size info)
			const estimatedLineHeight = Math.min(height, 16) // Assume typical line height
			const numberOfLines = Math.max(1, Math.floor(height / estimatedLineHeight))

			for (let i = 0; i < numberOfLines; i++) {
				const lineY = y + i * estimatedLineHeight
				const lineHeight = Math.min(estimatedLineHeight, height - i * estimatedLineHeight)

				// Skip lines with zero height
				if (lineHeight <= 0) continue

				try {
					const quad = new this.instance.Core.Math.Quad(
						x, // x1
						lineY, // y1
						x + width, // x2
						lineY, // y2
						x + width, // x3
						lineY + lineHeight, // y3
						x, // x4
						lineY + lineHeight, // y4
					)
					quads.push(quad)
				} catch (error) {
					console.warn("Failed to create Quad object:", error)
					// Fallback: create a simple quad object manually
					const fallbackQuad = {
						x1: x,
						y1: lineY,
						x2: x + width,
						y2: lineY,
						x3: x + width,
						y3: lineY + lineHeight,
						x4: x,
						y4: lineY + lineHeight,
						toRect: () => ({
							getWidth: () => width,
							getHeight: () => lineHeight,
						}),
					} as Core.Math.Quad
					quads.push(fallbackQuad)
				}
			}

			return quads.filter((quad) => {
				try {
					// Final validation by checking the quad bounds
					const rect = quad.toRect()
					return rect.getWidth() > 0 && rect.getHeight() > 0
				} catch (error) {
					// If toRect fails, do basic validation
					return quad.x2 > quad.x1 && quad.y3 > quad.y1
				}
			})
		} catch (error) {
			console.error("Error in createOptimizedQuadsFromBounds:", error)
			return []
		}
	}

	/**
	 * Public method to manually clear all highlights
	 */
	clearHighlights(): void {
		this.clearExistingHighlights()
	}

	/**
	 * Start a timer to automatically clear highlights after 10 seconds
	 */
	private startHighlightTimer(): void {
		// Clear any existing timer
		if (this.highlightTimer) {
			clearTimeout(this.highlightTimer)
		}

		// Set a new timer for 10 seconds
		this.highlightTimer = setTimeout(() => {
			this.clearExistingHighlights()
			this.highlightTimer = null
		}, 10000)
	}

	/**
	 * Clear all existing highlights when navigating to a new paragraph
	 */
	private clearExistingHighlights(): void {
		if (!this.instance) return

		try {
			// Clear the timer if it exists
			if (this.highlightTimer) {
				clearTimeout(this.highlightTimer)
				this.highlightTimer = null
			}

			const { annotationManager } = this.instance.Core

			// Remove all active highlights
			for (const annotation of this.activeHighlights) {
				try {
					annotationManager.deleteAnnotation(annotation)
				} catch (error) {
					console.warn("Could not remove existing highlight:", error)
				}
			}
			this.activeHighlights.clear()
		} catch (error) {
			console.warn("Error clearing existing highlights:", error)
		}
	}

	/**
	 * Set up simple click listener to clear highlights when clicking on empty areas
	 */
	private setupClickListener(): void {
		if (!this.instance) return

		try {
			const { documentViewer } = this.instance.Core

			// Use WebViewer's built-in click event - much simpler!
			documentViewer.addEventListener("click", () => {
				// Clear highlights on any click if we have active highlights
				if (this.activeHighlights.size > 0) {
					this.clearExistingHighlights()
				}
			})
		} catch (error) {
			console.warn("Failed to setup click listener:", error)
		}
	}

	/**
	 * Scrolls the viewer to the specific coordinates of the highlight using Apryse's built-in method
	 */
	private scrollToCoordinates(annotationData: TAnnotationData): void {
		if (!this.instance) return

		try {
			const { documentViewer } = this.instance.Core

			// Wait for the page to be fully loaded before scrolling
			setTimeout(() => {
				try {
					// Use Apryse WebViewer's built-in displayPageLocation method
					// This method handles coordinate transformation, zoom levels, and viewport positioning automatically
					documentViewer.displayPageLocation(
						annotationData.pageNumber,
						annotationData.x,
						annotationData.y,
						false, // always scroll to the location
					)
				} catch (error) {
					console.warn("Error scrolling to coordinates:", error)
					// Fallback: just ensure the page is visible
					try {
						documentViewer.setCurrentPage(annotationData.pageNumber, true)
					} catch (fallbackError) {
						console.warn("Fallback scroll also failed:", fallbackError)
					}
				}
			}, 100) // Small delay to ensure page is rendered
		} catch (error) {
			console.warn("Failed to scroll to coordinates:", error)
		}
	}

	/**
	 * Scrolls the viewer to the specific quads location for highlighted quotations
	 */
	private scrollToQuads(quads: unknown[], pageNumber: number): void {
		if (!this.instance || !quads || quads.length === 0) return

		try {
			const { documentViewer } = this.instance.Core

			// Calculate the center point of all quads for scrolling
			const validQuads = quads.filter((quad) => {
				const quadData = quad as {
					x1?: number
					y1?: number
					x2?: number
					y2?: number
					x3?: number
					y3?: number
					x4?: number
					y4?: number
				}
				return (
					quadData &&
					typeof quadData.x1 === "number" &&
					typeof quadData.y1 === "number" &&
					typeof quadData.x2 === "number" &&
					typeof quadData.y2 === "number" &&
					typeof quadData.x3 === "number" &&
					typeof quadData.y3 === "number" &&
					typeof quadData.x4 === "number" &&
					typeof quadData.y4 === "number"
				)
			})

			if (validQuads.length === 0) {
				console.warn("No valid quads for scrolling")
				return
			}

			// Find the overall bounding box of all quads
			const allX = validQuads.flatMap((quad) => {
				const q = quad as {
					x1: number
					y1: number
					x2: number
					y2: number
					x3: number
					y3: number
					x4: number
					y4: number
				}
				return [q.x1, q.x2, q.x3, q.x4]
			})
			const allY = validQuads.flatMap((quad) => {
				const q = quad as {
					x1: number
					y1: number
					x2: number
					y2: number
					x3: number
					y3: number
					x4: number
					y4: number
				}
				return [q.y1, q.y2, q.y3, q.y4]
			})

			const minX = Math.min(...allX)
			const minY = Math.min(...allY)

			// Calculate top-left point for better viewing - scroll to the beginning of the highlight
			const topX = minX
			const topY = minY

			// Wait for the page and highlight to be fully loaded before scrolling
			setTimeout(() => {
				try {
					// Use Apryse WebViewer's built-in displayPageLocation method
					// This method handles coordinate transformation, zoom levels, and viewport positioning automatically
					documentViewer.displayPageLocation(
						pageNumber,
						topX,
						topY,
						false, // always scroll to the location
					)
				} catch (error) {
					console.warn("Error scrolling to quads center:", error)
					// Fallback: just ensure the page is visible
					try {
						documentViewer.setCurrentPage(pageNumber, true)
					} catch (fallbackError) {
						console.warn("Fallback scroll also failed:", fallbackError)
					}
				}
			}, 150) // Slightly longer delay to ensure highlight is rendered
		} catch (error) {
			console.warn("Failed to scroll to quads:", error)
		}
	}

	/**
	 * Cleanup method
	 */
	cleanup(): void {
		// Clear timer
		if (this.highlightTimer) {
			clearTimeout(this.highlightTimer)
			this.highlightTimer = null
		}

		this.clearExistingHighlights()
		this.instance = null
	}
}

export default new ViewerNavigationService()
